import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, Plus, Minus, Settings, Trophy, Timer, Volume2, VolumeX } from 'lucide-react';
import { Card, Button, Input } from '../ui';
import { cn } from '../../lib/utils';

const TEAM_COLORS = [
  'bg-red-500',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-pink-500'
];

interface Team {
  id: number;
  name: string;
  score: number;
  color: string;
}

export function InteractiveScoreboard() {
  const [numTeams, setNumTeams] = useState<number>(2);
  const [teams, setTeams] = useState<Team[]>([
    { id: 1, name: 'Team 1', score: 0, color: TEAM_COLORS[0] },
    { id: 2, name: 'Team 2', score: 0, color: TEAM_COLORS[1] }
  ]);

  // Timer State
  const [initialTime, setInitialTime] = useState<number>(60); // in seconds
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  // Settings State
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // Auto-stop the timer when it reaches zero — the state change is a render-phase
  // adjustment (setState during render with a guard is sanctioned); the buzzer side
  // effect runs once via the timerHitZero flag.
  const [timerHitZero, setTimerHitZero] = useState(false);
  if (timeLeft === 0 && isRunning && !timerHitZero) {
    setTimerHitZero(true);
    setIsRunning(false);
  } else if (timeLeft !== 0 && timerHitZero) {
    setTimerHitZero(false);
  }

  // --- TIMER LOGIC ---
  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);

  useEffect(() => {
    if (timerHitZero && !isMuted) {
      // Play buzzer sound using Web Audio API or SpeechSynthesis as fallback
      if ('speechSynthesis' in window) {
        const msg = new SpeechSynthesisUtterance("Time is up!");
        msg.rate = 1.2;
        window.speechSynthesis.speak(msg);
      }
    }
  }, [timerHitZero, isMuted]);

  const toggleTimer = () => setIsRunning(!isRunning);
  
  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(initialTime);
  };

  const setTimerMinutes = (minutes: number) => {
    setInitialTime(minutes * 60);
    setTimeLeft(minutes * 60);
    setIsRunning(false);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // --- TEAM LOGIC ---
  // Adjust teams array when numTeams changes (render-phase adjustment).
  const [prevNumTeams, setPrevNumTeams] = useState(numTeams);
  if (numTeams !== prevNumTeams) {
    setPrevNumTeams(numTeams);
    if (numTeams > teams.length) {
      const newTeams = [...teams];
      for (let i = teams.length; i < numTeams; i++) {
        newTeams.push({
          id: i + 1,
          name: `Team ${i + 1}`,
          score: 0,
          color: TEAM_COLORS[i]
        });
      }
      setTeams(newTeams);
    } else if (numTeams < teams.length) {
      setTeams(teams.slice(0, numTeams));
    }
  }

  const updateScore = (id: number, delta: number) => {
    setTeams(teams.map(t => t.id === id ? { ...t, score: Math.max(0, t.score + delta) } : t));
  };

  const updateName = (id: number, name: string) => {
    setTeams(teams.map(t => t.id === id ? { ...t, name } : t));
  };

  const getWinner = () => {
    if (teams.length === 0) return null;
    const maxScore = Math.max(...teams.map(t => t.score));
    if (maxScore === 0) return null;
    return teams.filter(t => t.score === maxScore);
  };

  const winners = getWinner();

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-4">
      {/* Top Bar: Timer & Settings Toggle */}
      <div className="bg-slate-900 rounded-2xl shadow-xl flex flex-col md:flex-row items-center justify-between p-6 text-white border-4 border-slate-800">
        
        {/* Settings Toggle */}
        <div className="flex items-center mb-6 md:mb-0">
          <Button 
            variant="secondary" 
            onClick={() => setShowSettings(!showSettings)}
            className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Settings className="w-5 h-5 mr-2" />
            {showSettings ? 'Hide Settings' : 'Setup Game'}
          </Button>
        </div>

        {/* Central Timer Display */}
        <div className="flex flex-col items-center">
          <div className="text-sm font-bold tracking-widest text-slate-400 uppercase mb-2 flex items-center">
            <Timer className="w-4 h-4 mr-2" /> Match Timer
          </div>
          <div className={cn(
            "text-6xl md:text-8xl font-black tabular-nums tracking-tighter drop-shadow-lg",
            timeLeft <= 10 && timeLeft > 0 ? "text-red-500 animate-pulse" : "text-white"
          )}>
            {formatTime(timeLeft)}
          </div>
          <div className="flex gap-4 mt-4">
            <Button 
              size="lg" 
              onClick={toggleTimer}
              className={cn(
                "w-32 font-bold text-lg",
                isRunning ? "bg-amber-500 hover:bg-amber-600 text-black" : "bg-emerald-500 hover:bg-emerald-600 text-white"
              )}
            >
              {isRunning ? <><Pause className="w-5 h-5 mr-2" /> Pause</> : <><Play className="w-5 h-5 mr-2" /> Start</>}
            </Button>
            <Button 
              size="lg" 
              variant="secondary" 
              onClick={resetTimer}
              className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="secondary" 
              onClick={() => setIsMuted(!isMuted)}
              className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Branding placeholder for symmetry */}
        <div className="hidden md:flex flex-col items-end opacity-50">
          <Trophy className="w-12 h-12 text-slate-600 mb-2" />
          <div className="text-xl font-black tracking-widest uppercase">Scoreboard</div>
        </div>
      </div>

      {/* Settings Panel (Expandable) */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="p-6 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-wrap gap-8 items-start">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Number of Teams</label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6].map(num => (
                    <button
                      key={num}
                      onClick={() => setNumTeams(num)}
                      className={cn(
                        "w-12 h-12 rounded-lg font-black text-xl transition-all",
                        numTeams === num ? "bg-indigo-600 text-white shadow-md scale-110" : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-100"
                      )}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Timer Presets</label>
                <div className="flex gap-2">
                  {[1, 5, 10, 15, 30].map(mins => (
                    <Button 
                      key={mins} 
                      variant="secondary" 
                      onClick={() => setTimerMinutes(mins)}
                      className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                    >
                      {mins}m
                    </Button>
                  ))}
                </div>
              </div>

              <div className="ml-auto">
                <Button 
                  variant="danger" 
                  onClick={() => {
                    if(window.confirm('Reset all scores to zero?')) {
                      setTeams(teams.map(t => ({ ...t, score: 0 })));
                    }
                  }}
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset All Scores
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Scoreboard Grid */}
      <div className={cn(
        "grid gap-4 md:gap-6",
        numTeams === 2 ? "grid-cols-1 md:grid-cols-2" : 
        numTeams === 3 ? "grid-cols-1 md:grid-cols-3" : 
        numTeams === 4 ? "grid-cols-2 md:grid-cols-2 lg:grid-cols-4" : 
        "grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
      )}>
        <AnimatePresence mode="popLayout">
          {teams.map((team) => {
            const isWinner = winners && winners.some(w => w.id === team.id) && team.score > 0;
            return (
              <motion.div
                key={team.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className={cn(
                  "relative rounded-3xl shadow-xl overflow-hidden border-4 transition-all duration-300 flex flex-col",
                  isWinner ? "border-yellow-400 shadow-[0_0_30px_rgba(250,204,21,0.5)] transform -translate-y-2" : "border-transparent"
                )}
              >
                {/* Team Header */}
                <div className={cn("p-4 md:p-6 text-center text-white", team.color)}>
                  <Input 
                    value={team.name} 
                    onChange={(e) => updateName(team.id, e.target.value)}
                    className="text-center text-2xl md:text-3xl font-black uppercase bg-white/10 border-white/20 text-white placeholder:text-white/50 h-16 shadow-inner focus:bg-white/20"
                  />
                  {isWinner && (
                    <div className="absolute top-0 right-0 p-3">
                      <Trophy className="w-8 h-8 text-yellow-300 drop-shadow-md animate-bounce" />
                    </div>
                  )}
                </div>

                {/* Score Area */}
                <div className="flex-1 bg-white dark:bg-slate-800 p-6 flex flex-col items-center justify-center min-h-[250px]">
                  <motion.div 
                    key={team.score}
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-8xl lg:text-[10rem] font-black tabular-nums tracking-tighter text-slate-900 dark:text-white drop-shadow-sm leading-none py-8"
                  >
                    {team.score}
                  </motion.div>
                </div>

                {/* Score Controls */}
                <div className="grid grid-cols-2 gap-1 bg-slate-100 dark:bg-slate-900 p-1">
                  <button 
                    onClick={() => updateScore(team.id, -1)}
                    className="h-24 md:h-32 flex items-center justify-center bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-400 hover:text-red-500 transition-colors rounded-bl-2xl border border-slate-200 dark:border-slate-700"
                  >
                    <Minus className="w-12 h-12" />
                  </button>
                  <button 
                    onClick={() => updateScore(team.id, 1)}
                    className="h-24 md:h-32 flex items-center justify-center bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-400 hover:text-emerald-500 transition-colors rounded-br-2xl border border-slate-200 dark:border-slate-700 shadow-sm"
                  >
                    <Plus className="w-12 h-12" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
